/**
 * getTheme - Returns the active theme. Single-mode (dark); ThemeMode is kept
 * as a literal type for downstream call-site compatibility.
 */

import type { ThemeMode } from './ThemeContext';
import { darkTheme, type ThemeColors } from './themes';

// Shared theme properties that don't change between themes
const sharedThemeProperties = {
  // Typography scale (inspired by sign-in page)
  typography: {
    // Headings
    h1: {
      fontSize: 24,
      fontWeight: '600' as const,
      lineHeight: 32,
    },
    h2: {
      fontSize: 20,
      fontWeight: '600' as const,
      lineHeight: 28,
    },
    h3: {
      fontSize: 18,
      fontWeight: '600' as const,
      lineHeight: 24,
    },
    // Body text
    body: {
      fontSize: 15,
      fontWeight: '400' as const,
      lineHeight: 22,
    },
    bodySmall: {
      fontSize: 14,
      fontWeight: '400' as const,
      lineHeight: 20,
    },
    bodyLarge: {
      fontSize: 16,
      fontWeight: '400' as const,
      lineHeight: 24,
    },
    // Labels and buttons
    label: {
      fontSize: 15,
      fontWeight: '500' as const,
      lineHeight: 22,
    },
    labelSmall: {
      fontSize: 13,
      fontWeight: '500' as const,
      lineHeight: 18,
    },
    // Captions
    caption: {
      fontSize: 13,
      fontWeight: '400' as const,
      lineHeight: 18,
    },
  },

  // Spacing scale (consistent spacing throughout app)
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
    xxxl: 28,
    huge: 32,
  },

  // Border radius (rounded corners for cards, inputs, buttons)
  borderRadius: {
    sm: 8,
    md: 10, // Inputs and buttons
    lg: 12, // Cards
    xl: 16,
    full: 9999,
  },

  // Shadows will be overridden per theme
  shadows: {} as {
    card: string;
    cardElevated: string;
    button: string;
  },

  // Touch targets (minimum sizes for interactive elements)
  touchTargets: {
    minHeight: 44, // Minimum button height
    minWidth: 44, // Minimum button width
  },

  // Card styles
  card: {
    borderRadius: 12,
    padding: 32,
  },

  // Input styles
  input: {
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 15,
    minHeight: 44,
  },

  // Button styles
  button: {
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 20,
    minHeight: 44,
    fontSize: 15,
    fontWeight: '500' as const,
  },
};

export type Theme = ThemeColors & typeof sharedThemeProperties;

const DARK_SHADOWS = {
  card: '0 2px 4px rgba(0, 0, 0, 0.4)',
  cardElevated: '0 6px 12px rgba(0, 0, 0, 0.5)',
  button: '0 2px 4px rgba(167, 139, 250, 0.3)',
};

export const getTheme = (_themeMode?: ThemeMode): Theme => {
  const colors: ThemeColors = darkTheme;
  return {
    ...colors,
    ...sharedThemeProperties,
    shadows: DARK_SHADOWS,
    card: {
      ...sharedThemeProperties.card,
      backgroundColor: colors.background.card,
    } as typeof sharedThemeProperties.card,
    input: {
      ...sharedThemeProperties.input,
      borderColor: colors.gray[200],
    } as typeof sharedThemeProperties.input,
  };
};
